'use es6';

import { forwardRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { getEmptyImage } from 'react-dnd-compat';
import PropTypes from 'prop-types';
import { View } from 'foundations-components/components/View';
import MenuItem from 'simple-menus-lib/components/menu/MenuItem';
import MenuNodeDivider from 'simple-menus-lib/components/menu/MenuNodeDivider';
import menuDragSource from 'simple-menus-lib/components/dnd/decorators/menuDragSource';
import { setIsDragging } from 'simple-menus-core/actions/Application';
import { DropPositions } from 'simple-menus-lib/constants/dragAndDrop';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TopDivider = ({
  isDragging,
  isParentDragging,
  readOnly,
  id,
  menuId,
  parentId,
  depth,
  index
}) => {
  if (index === 0 && depth === 0) {
    return /*#__PURE__*/_jsx(MenuNodeDivider, {
      isParentDragging: isDragging || isParentDragging,
      readOnly: readOnly,
      id: id,
      menuId: menuId,
      parentId: parentId,
      depth: depth,
      position: DropPositions.TOP
    });
  }
  return null;
};
const MenuNode = /*#__PURE__*/forwardRef((props, ref) => {
  const [collapsed, setIsCollapsed] = useState(false);
  const {
    hasSibling,
    isDragging,
    isLastChild,
    isParentDragging,
    readOnly,
    children = [],
    depth,
    depthToNextSibling,
    hierarchy,
    index,
    id,
    menuId,
    parentId,
    connectDragSource,
    connectDragPreview,
    getDOMElementRef,
    getMenuComponent,
    moveNode
  } = props;
  const nodeOpacity = isDragging ? 0.5 : 1;
  const MenuTreeComponent = getMenuComponent(true);
  useEffect(() => {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [connectDragPreview]);
  const toggleMenuTreeCollapse = () => {
    setIsCollapsed(isCollapsed => !isCollapsed);
  };
  const viewRefCallback = element => {
    // Forward the ref to the original ref prop
    if (ref) {
      if (typeof ref === 'function') {
        ref(element);
      } else {
        ref.current = element;
      }
    }
    // Register the DOM element for access by the drag source
    if (getDOMElementRef) {
      getDOMElementRef(element);
    }
  };
  return connectDragSource( /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsxs(View, {
      id: `menu-node-${id}`,
      opacity: nodeOpacity,
      ref: viewRefCallback,
      children: [/*#__PURE__*/_jsx(TopDivider, {
        isDragging: isDragging,
        isParentDragging: isParentDragging,
        readOnly: readOnly,
        id: id,
        menuId: menuId,
        parentId: parentId,
        depth: depth,
        index: index
      }), /*#__PURE__*/_jsx(MenuItem, {
        hasChildren: Boolean(children.length),
        isCollapsed: collapsed,
        isParentDragging: isDragging || isParentDragging,
        readOnly: readOnly,
        id: id,
        menuId: menuId,
        parentId: parentId,
        depth: depth,
        moveNode: moveNode,
        toggleCollapse: toggleMenuTreeCollapse
      }), /*#__PURE__*/_jsx(MenuNodeDivider, {
        hasChildren: !!children.length,
        hasSibling: hasSibling,
        isCollapsed: collapsed,
        isParentDragging: isDragging || isParentDragging,
        isLastChild: isLastChild,
        readOnly: readOnly,
        id: id,
        menuId: menuId,
        parentId: collapsed ? null : parentId,
        depth: depth,
        depthToNextSibling: depthToNextSibling,
        hierarchy: hierarchy
      }), /*#__PURE__*/_jsx(MenuTreeComponent, {
        isCollapsed: collapsed,
        isParentDragging: isDragging || isParentDragging,
        readOnly: readOnly,
        menuId: menuId,
        parentId: id,
        nodes: children,
        depth: depth + 1,
        depthToNextSibling: depthToNextSibling,
        hierarchy: [...hierarchy, id],
        getMenuComponent: getMenuComponent,
        moveNode: moveNode
      })]
    })
  }));
});
MenuNode.propTypes = {
  hasSibling: PropTypes.bool,
  isDragging: PropTypes.bool,
  isLastChild: PropTypes.bool,
  isParentDragging: PropTypes.bool,
  readOnly: PropTypes.bool,
  children: PropTypes.arrayOf(PropTypes.any).isRequired,
  depth: PropTypes.number.isRequired,
  depthToNextSibling: PropTypes.number,
  hierarchy: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  index: PropTypes.number.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  connectDragSource: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  getDOMElementRef: PropTypes.func,
  getMenuComponent: PropTypes.func.isRequired,
  moveNode: PropTypes.func.isRequired
};
export default compose(connect(null, {
  setIsDragging
}), menuDragSource())(MenuNode);